<template>
  <div class="goods-list qm-page-bg">
    <van-nav-bar :title="shopName" left-arrow class="qm-nav-bar" @click-left="$router.back()" />

    <div class="coupon-bar" v-if="memberCoupon">
      <van-icon name="coupon-o" />
      <span>{{ memberCoupon.couponName }}</span>
      <em v-if="memberCoupon.onlyMedium">仅中杯</em>
    </div>

    <div class="search-container">
      <van-search
        v-model="searchKeyword"
        placeholder="搜索商品名称..."
        shape="round"
        clearable
      />
    </div>

    <div class="goods-container">
      <div
        v-for="item in filteredGoods"
        :key="item.spuCode"
        class="goods-item qm-glass-card"
        @click="openDetail(item)"
      >
        <div class="goods-image">
          <img :src="item.imageUrl" :alt="item.spuName" />
          <div class="sold-out-mask" v-if="item.disableFlag === '1'">
            <span>售罄</span>
          </div>
        </div>
        <div class="goods-content">
          <div class="goods-info">
            <h3 class="goods-name">{{ item.spuName }}</h3>
            <div class="goods-desc">{{ item.menuDesc }}</div>
          </div>
          <div class="goods-action">
            <div class="price">
              <span class="now">¥{{ fenToYuan(item.finalPrice != null ? item.finalPrice : 0) }}</span>
              <span class="origin" v-if="item.orgPrice">¥{{ fenToYuan(item.orgPrice) }}</span>
            </div>
            <div class="add-btn" :class="{ disabled: item.disableFlag === '1' }">
              <van-icon name="plus" />
            </div>
          </div>
        </div>
      </div>
      <van-empty v-if="!loading && !filteredGoods.length" description="暂无可用商品" />
    </div>

    <van-popup
      v-model="showDetail"
      position="bottom"
      round
      closeable
      :style="{ height: '72%' }"
    >
      <div class="detail-popup" v-if="detail">
        <div class="popup-handle"></div>
        <div class="scrollable-content">
          <div class="goods-image">
            <img :src="detail.imageUrl" :alt="detail.spuName" />
          </div>
          <div class="goods-info">
            <h1 class="goods-name">{{ detail.spuName }}</h1>
            <div class="goods-desc">{{ detail.menuDesc }}</div>
          </div>

          <div class="specs-section">
            <div class="section-title">规格{{ detail.onlyMedium ? '（仅中杯）' : '' }}</div>
            <div class="options-list">
              <div
                v-for="spec in detail.specs"
                :key="spec.skuCode"
                class="option-item"
                :class="{
                  active: selectedSku === spec.skuCode,
                  disabled: spec.disableFlag === '1',
                }"
                @click="selectSku(spec)"
              >
                {{ spec.skuName }}
              </div>
            </div>
          </div>
        </div>

        <div class="bottom-bar">
          <van-button
            type="primary"
            block
            class="qm-btn-primary"
            :loading="ordering"
            :disabled="!selectedSku"
            @click="submitOrder"
          >
            确认下单
          </van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import request from "@/utils/request";
import { fenToYuan } from "@/utils/tims";

export default {
  name: "GoodsList",
  data() {
    return {
      shopName: "",
      storeCode: "",
      goodsList: [],
      searchKeyword: "",
      loading: false,
      showDetail: false,
      detail: null,
      selectedSku: "",
      currentGoods: null,
      ordering: false,
    };
  },
  computed: {
    ...mapState(["uuid", "memberCoupon", "appConfig"]),
    filteredGoods() {
      const kw = this.searchKeyword.trim();
      if (!kw) return this.goodsList;
      return this.goodsList.filter((g) => (g.spuName || "").includes(kw));
    },
  },
  created() {
    if (!this.uuid) {
      this.$router.replace("/cardkey");
      return;
    }
    this.shopName = this.$route.query.shopName || "点单";
    this.storeCode = this.$route.query.storeCode || "";
    this.fetchGoods();
  },
  methods: {
    ...mapActions(["setLastOrder", "setMemberCoupon"]),
    fenToYuan,
    async fetchGoods() {
      this.loading = true;
      const toast = this.$toast.loading({ message: "加载中...", forbidClick: true, duration: 0 });
      try {
        const resp = await request.post("/tims/goods-list", {
          uuid: this.uuid,
          storeCode: this.storeCode,
          lat: this.$route.query.lat,
          lng: this.$route.query.lng,
        });
        this.goodsList = (resp.data && resp.data.goods) || [];
        if (resp.data && resp.data.coupon) {
          await this.setMemberCoupon(resp.data.coupon);
        }
        if (!this.goodsList.length) {
          this.$toast("该门店暂无券内商品");
        }
      } catch (e) {
        this.$toast.fail(e.message || "加载失败");
      } finally {
        toast.clear();
        this.loading = false;
      }
    },
    async openDetail(item) {
      if (item.disableFlag === "1") {
        this.$toast("该商品售罄");
        return;
      }
      const toast = this.$toast.loading({ message: "加载规格...", forbidClick: true, duration: 0 });
      try {
        const resp = await request.post("/tims/goods-detail", {
          uuid: this.uuid,
          storeCode: this.storeCode,
          spuCode: item.spuCode,
        });
        this.detail = resp.data;
        this.currentGoods = item;
        const specs = (this.detail && this.detail.specs) || [];
        const first = specs.find((s) => s.disableFlag !== "1") || specs[0];
        this.selectedSku = first ? first.skuCode : "";
        this.showDetail = true;
      } catch (e) {
        this.$toast.fail(e.message || "详情失败");
      } finally {
        toast.clear();
      }
    },
    selectSku(spec) {
      if (spec.disableFlag === "1") return;
      this.selectedSku = spec.skuCode;
    },
    async submitOrder() {
      if (!this.selectedSku || !this.detail) return;
      this.ordering = true;
      try {
        const resp = await request.post("/tims/order", {
          uuid: this.uuid,
          storeCode: this.storeCode,
          spuCode: this.detail.spuCode,
          skuCode: this.selectedSku,
          quantity: 1,
        });
        const data = resp.data || {};
        await this.setLastOrder({
          ...data,
          shopName: this.shopName,
          createdAt: Date.now(),
        });
        this.showDetail = false;
        this.$toast.success("下单成功");
        this.$router.replace({
          path: "/order-detail",
          query: { orderNo: data.orderId },
        });
      } catch (e) {
        this.$toast.fail(e.message || "下单失败");
      } finally {
        this.ordering = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.goods-list {
  min-height: 100vh;
  padding-bottom: 24px;
}

.coupon-bar {
  margin: 8px 16px 0;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(200, 16, 46, 0.08);
  color: #c8102e;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;

  em {
    margin-left: auto;
    font-style: normal;
    background: #c8102e;
    color: #fff;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10px;
  }
}

.search-container {
  padding: 8px 12px 0;
}

.goods-container {
  padding: 8px 16px;
}

.goods-item {
  display: flex;
  padding: 12px;
  margin-bottom: 10px;
  gap: 12px;
}

.goods-image {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  flex-shrink: 0;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .sold-out-mask {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
  }
}

.goods-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}

.goods-name {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 4px;
}

.goods-desc {
  font-size: 12px;
  color: #999;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.goods-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;

  .price {
    .now {
      color: #c8102e;
      font-weight: 700;
      font-size: 16px;
      margin-right: 6px;
    }
    .origin {
      color: #bbb;
      font-size: 12px;
      text-decoration: line-through;
    }
  }

  .add-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #c8102e;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;

    &.disabled {
      background: #ccc;
    }
  }
}

.detail-popup {
  height: 100%;
  display: flex;
  flex-direction: column;

  .popup-handle {
    width: 36px;
    height: 4px;
    background: #ddd;
    border-radius: 2px;
    margin: 10px auto 0;
  }

  .scrollable-content {
    flex: 1;
    overflow: auto;
    padding: 12px 16px 80px;
  }

  .goods-image {
    width: 100%;
    height: 180px;
    border-radius: 14px;
    margin-bottom: 12px;
  }

  .goods-name {
    font-size: 18px;
  }

  .specs-section {
    margin-top: 18px;
  }

  .section-title {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
  }

  .options-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .option-item {
    padding: 8px 14px;
    border-radius: 8px;
    background: #f5f5f5;
    font-size: 13px;

    &.active {
      background: rgba(200, 16, 46, 0.12);
      color: #c8102e;
      font-weight: 600;
    }

    &.disabled {
      opacity: 0.4;
    }
  }

  .bottom-bar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: #fff;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.04);
  }
}
</style>
